import {useTranslation} from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import React, {Fragment, useEffect, useState} from "react";
import _, {filter, isEmpty} from "lodash";
import moment from "moment/moment";
import {Chip, IconButton, Paper, Tooltip} from '@mui/material';
import config from "../../../app/config";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import {useDispatch, useSelector} from 'react-redux';
import {referredToService} from "../../../store/reducers/dicts";
import {PadlockCloseIcon} from '../../icons/icons';
import AgyleTextField from '../../ui/fields/agyleTextField';
import CloseIcon from '@mui/icons-material/Close';
import {getSingleBedasSessions} from '../../../store/reducers/ward';
import Typography from '@mui/material/Typography';

const useStyles = makeStyles((theme) => ({
    center: {
        paddingTop: 5,
        textAlign: "center",
        width: "100%",
        fontSize: "small",
        color: theme.palette.mode === "light" ? "#000" : "#FFF",
    },
    container: {
        backgroundColor: theme.palette.mode === "light" ? "#FFF" : "#313233",
        borderRadius: 4,
        width: "100%",
        height: 80,
        cursor: "pointer",
        fontWeight: "bold",
        "&.disabled": {
            opacity: 0.6,
            cursor: "no-drop",
            // backgroundColor: "#ACAFB3",
            // backgroundColor: "#87888a",
            // backgroundColor: "#6e6f70",
            backgroundColor: "black",

        },
        "&.available": {
            backgroundColor: theme.palette.mode === "light" ? theme.palette.primary.light : theme.palette.primary.main,
        },
    },
    day: {
        textAlign: "center",
        backgroundColor: theme.palette.mode === "light" ? "#D2D5D9" : "#3D3E40",
        borderRadius: "4px 4px 0 0",
        "&.currentDay": {
            backgroundColor: theme.palette.primary.light,
        },
    },
    daily: {
        position: "absolute",
        background: theme.palette.background.paper,
        padding: 5,
    },
    "@keyframes blinker": {
        from: { border: '5px solid #448FFF' },
        to: { border: '5px solid transparent' }
    },
    session: {
        position: "absolute",
        backgroundColor: "#6e6f70",
        padding: 5,
        border: "1px solid #000",

        "&.locked": {
            backgroundColor: theme.palette.background.default,
        },

        '&.dark': {
            backgroundColor: theme.palette.background.default,
        },

        "&.temporary": {
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'white',
            color: 'black',
            animationName: "$blinker",
            animationDuration: "2s",
            animationDirection: "alternate",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
            zIndex: 99
        },
    },
    divRow: {
        display: "flex",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "center",
    },
    ptName: {
        fontWeight: "bold",
        fontSize: 15,
    },
    date: {
        fontStyle: "italic",
        fontSize: "small",
        marginLeft: 5,
    },
    gender: {
        marginLeft: 5,
        borderRadius: 5,
        width: 28,
        height: 28,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#3D3E40",
        "&.male": {
            backgroundColor: "#92BFE2",
        },
        "&.female": {
            backgroundColor: "#F4BDD1",
        },
        "&.unspecified": {
            color: "#FFF",
            backgroundColor: "#313233",
        },
    },
    chip: {
        marginLeft: 10,
        marginRight: 10,
    },
    referral: {
        display: "inline-block",
        width: 40,
        margin: 1,
        height: 28,
        textAlign: "center",
        lineHeight: "30px",
        color: "#fff",
        borderRadius: 10,
        fontSize: "small",
        "&.completed": {
            border: "2px solid #000",
            color: "#000",
            lineHeight: "25px",
        },
    },
    lockedContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        zIndex: 30,
        width: '100%'
    },
    temporaryLockedContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        zIndex: 30,
        backgroundColor: 'white',
    },
    darkSession: {
        backgroundColor: theme.palette.background.default,
        '&.MuiPaper-root': {
            backgroundImage: `linear-gradient(to right, ${theme.palette.background.default}, ${theme.palette.background.default})`,
        },
    }
}));

const BedasCalendarElement = ({
                                  day,
                                  isFromOtherMonth,
                                  handleSelectDate,
                                  events,
                                  calendarType,
                                  event,
                                  options,
                                  isDisabled,
                                  session,
                                  singleSession,
                                  onSelectTime,
                                  locked,
                                  temporaryLock,
                                  changeLockedReason,
                                  deleteTemporaryLockedSession,
                                  darkMode,
                              }) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const [eventsCount, setEventsCount] = useState(0);
    const dictReferredToService = useSelector(referredToService);

    const oldCal = config?.modules?.bedas?.bedasOldCalendar;

    useEffect(() => {
        const start = moment(day).startOf("day");
        const end = moment(day).endOf("day");

        if (!isEmpty(events) && !isFromOtherMonth) {
            const eventCurrent = filter(events, pt => {
                if (pt?.bedas?.date) {
                    return moment(pt?.bedas?.date).isSameOrAfter(start) && moment(pt?.bedas?.date).isSameOrBefore(end);
                }
            });
            setEventsCount(eventCurrent.length);
        }
    }, [events]);

    const handleSelect = () => {
        if (!isFromOtherMonth) {
            handleSelectDate(day);
        }
    };

    const getGenderElement = (code) => {
        let el = null;
        // eslint-disable-next-line default-case
        switch (code) {
            case "1": //male
                el = <div className={clsx(classes.gender, "male")}><MaleIcon/></div>;
                break;
            case "2": // female
                el = <div className={clsx(classes.gender, "female")}><FemaleIcon/></div>;
                break;
            case "9": // Unspecified
                el = <div className={clsx(classes.gender, "unspecified")}>U</div>;
                break;
        }

        return el;
    };

    const getChiefComplain = (pt) => {
        if (pt.clerking?.chiefComplaint) return pt.clerking?.chiefComplaint?.__description;
        if (pt?.triage?.chiefComplaint) return pt?.triage?.chiefComplaint?.__description;
        if (pt?.admission?.initialAEComplaintDescription) return pt?.admission?.initialAEComplaintDescription;
        return "-";
    };

    const getReferrals = (patient) => {
        const getReferralsColor = (ecdsObj) => {
            if (ecdsObj) {
                let color = config.referralsColors[ecdsObj.group ? ecdsObj.group : ecdsObj.type];
                if (!_.isEmpty(config.referralsColors.exceptions)) {
                    const find = _.find(config.referralsColors.exceptions, {code: ecdsObj.snomedCode ? ecdsObj.snomedCode : ecdsObj.c});
                    color = find ? find?.color : color;
                }
                return color;
            }
        };

        const referrals = [];

        if (!_.isEmpty(dictReferredToService) && !_.isEmpty(patient?.studies?.referrals?.ReferralsToOtherServices)) {
            _.forEach(patient?.studies?.referrals?.ReferralsToOtherServices, referral => {
                const find = _.find(dictReferredToService, {__id: referral?.ReferredToService_SnomedCt?.__id});
                if (find)
                    referrals.push({
                        id: find.notes,
                        group: find.group,
                        description: find.__description,
                        snomedCode: find.snomedCode,
                        completed: referral.ReferredToServiceAssessmentTime !== "",
                    });
            });
        }

        if (_.isEmpty(referrals)) return "-";
        return (
            <Fragment>
                {referrals.map((ref, index) => (
                    <Tooltip
                        key={index}
                        title={ref.description}
                    >
                        <div
                            style={{backgroundColor: getReferralsColor(ref)}}
                            className={clsx(classes.referral, ref.completed && "completed")}
                        >
                            {ref.id}
                        </div>
                    </Tooltip>
                ))}
            </Fragment>
        );
    };

    const renderSession = () => {
       return <div onClick={onSelectTime} />
    }

    const renderLocked = () => (
      <div className={clsx(classes.lockedContainer)} style={{ justifyContent: !temporaryLock ? 'center' : 'space-between'}}>
          {!temporaryLock && (
              <>
                  <PadlockCloseIcon />
                  <Typography variant={"subtitle1"}>{(t(event?.lockedReason) || t("Locked"))}</Typography>
              </>
          )}

          {temporaryLock && event?.locked && (
              <>
                  <AgyleTextField
                      value={event?.lockedReason}
                      onClick={(e) => e.stopPropagation()}
                      onChange={e => {
                          e.stopPropagation();
                          changeLockedReason(event, e.target.value)}
                      }
                      style={{width: '100%', marginTop: 3, justifySelf: 'center', marginLeft: "30%", marginRight: '30%'}}
                      label={t("Reason")}
                  />

                  <IconButton
                      style={{color: 'black'}}
                      onClick={(e) => {
                          e.stopPropagation();
                          deleteTemporaryLockedSession(event?.id)}
                      }
                  >
                      <CloseIcon  />
                  </IconButton>
              </>
          )}
      </div>)

    const renderDailyComponent = () => {
        const age = moment().diff(event?.patient?.dob, "years");
        const gender = getGenderElement(event?.patient?.genderCode);
        const chief = getChiefComplain(event);
        const referrals = getReferrals(event);
        const reason = event?.bedas?.reason || "-";

        return (
            <React.Fragment>
                <div className={clsx("first-level-level", classes.divRow)}>
                    <div className={classes.ptName}>
                        {`${event?.patient?.familyName} ${event?.patient?.firstName}`}
                    </div>

                    <div className={classes.date}>
                        {moment(event?.bedas?.date).format(config.defaultDateTimeFormat)}
                    </div>
                </div>

                <div className={clsx("second-level", classes.divRow)}>
                    {event?.patient?.__id} | {t("Age")} {`${age}`} {gender || event?.patient?.gender}
                </div>

                <div className={clsx("third-level", classes.divRow)}>
                    CC:
                    <Chip
                        className={clsx(classes.chip)}
                        size="small"
                        label={chief}
                    />
                    {t("Reason")}:
                    <div style={{marginLeft: 5}}>{reason}</div>
                </div>
            </React.Fragment>
        );
    };

    const isCurrentDay = (date) => {
        return moment(date).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
    };

    const returnElementType = () => {
        // eslint-disable-next-line default-case
        switch (calendarType) {
            case "monthly":
                return (
                    <div
                        className={
                            clsx(
                                classes.container,
                                eventsCount > 0 && "available",
                                isFromOtherMonth && "disabled",
                                isDisabled && "disabled",
                            )
                        }
                        onClick={!isDisabled && handleSelect}
                        key={Math.random()}
                    >
                        <div className={clsx(classes.day, isCurrentDay(day) && "currentDay")}>
                            {day.format("D")}
                        </div>

                        <div className={classes.center}>
                            {t("Patients booked")}
                            <br/>
                            {eventsCount}
                        </div>
                    </div>
                );
            case "daily":
                if (session) {
                    return <Paper
                      key={event.__id}
                      className={clsx(classes.session, locked && 'locked', temporaryLock && 'temporary', darkMode && classes.darkSession)}
                      style={{
                          top: options.top,
                          left: options.left,
                          height: options.height,
                          width: options.width,
                          zIndex: locked ? 2 : 1,
                      }}
                      onClick={!locked ? onSelectTime : null}
                    >
                        {locked || temporaryLock ? renderLocked() : renderSession()}
                    </Paper>
                } else {
                    return (
                    <Paper
                        key={event.__id}
                        className={classes.daily}
                        onClick={onSelectTime}
                        style={{
                            top: options.top,
                            left: options.left,
                            height: options.height,
                            width: options.width,
                            zIndex: 100,
                        }}
                    >
                        {renderDailyComponent()}
                    </Paper>
                );}
        }
    };

    return returnElementType();
};

export default BedasCalendarElement;